import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  FIRESTORE_COLLECTION,
  FIRESTORE_DOCUMENT,
  InAppUpdateProductCategoryPayload,
  ProductResourcesTreeData,
} from '@mailingr/data-models';

import { ActionParams, AsyncThunkCreator } from '../../../index';
import { PRODUCT_RESOURCES_REDUCER_NAME } from '../types';

type Payload = {
  productId: string;
  model: InAppUpdateProductCategoryPayload;
};

export const updateProductResourceCategory = createAsyncThunk<
  ProductResourcesTreeData | null,
  ActionParams<Payload, string>,
  AsyncThunkCreator<InAppUpdateProductCategoryPayload | number>
>(
  `${PRODUCT_RESOURCES_REDUCER_NAME}/updateProductResourceCategory`,
  async (
    { payload, onSuccess, onFailure },
    { rejectWithValue, extra: { db, auth, analytics } }
  ) => {
    try {
      const user = auth().currentUser;

      if (!user) {
        throw new Error('invalid-user');
      }

      const model: InAppUpdateProductCategoryPayload = {
        ...payload.model,
      };

      let categoryModel: ProductResourcesTreeData | null = null;
      await db.runTransaction(async (transaction: any) => {
        const ref = db
          .collection(FIRESTORE_COLLECTION.USERS)
          .doc(user.uid)
          .collection(FIRESTORE_COLLECTION.PRODUCTS_LIST)
          .doc(payload.productId)
          .collection(FIRESTORE_COLLECTION.PRODUCT_RESOURCES)
          .doc(FIRESTORE_DOCUMENT.PRODUCT_RESOURCES_SETTINGS);

        const snapshot = await transaction.get(ref);

        const data = snapshot.data();
        if (data) {
          await transaction.update(ref, {
            categoriesTree: data.categoriesTree.map((category: ProductResourcesTreeData) =>
              category.key === model.id ? { ...category, title: model.name } : category
            ),
          });

          categoryModel = data.categoriesTree.find(
            (category: ProductResourcesTreeData) => category.key === model.id
          );
          if (categoryModel) {
            const resourceRef = db
              .collection(FIRESTORE_COLLECTION.USERS)
              .doc(user.uid)
              .collection(FIRESTORE_COLLECTION.PRODUCTS_LIST)
              .doc(payload.productId)
              .collection(FIRESTORE_COLLECTION.PRODUCT_RESOURCES);

            for (const child of categoryModel.children) {
              if (child.type === 'resource') {
                await transaction.update(resourceRef.doc(child.key), {
                  category: model.name,
                  parentId: model.id,
                  updatedAt: new Date(),
                });
              }
            }
          }
        }
      });

      await analytics.track('update_resource_category_to_product', {
        productId: payload.productId,
        userId: user.uid,
      });

      onSuccess?.();

      return null;
    } catch (e) {
      onFailure?.();

      return rejectWithValue(e);
    }
  }
);
